import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { NavtaraView } from './NavtaraView';
import { TrinesView } from './TrinesView';
import BirthTimeRectification from './BirthTimeRectification';
import { AstrologerCoreView } from './AstrologerCoreView';
import { KarkaView } from './KarkaView';
import { MuhurtaAdvisor } from './MuhurtaAdvisor';
import { PrashnaModule } from './PrashnaModule';
import { VarshaphalaModule } from './VarshaphalaModule';
import { PlanetStrengthDashboard } from './PlanetStrengthDashboard';
import { Star, Triangle, Clock, Shield, Award, Calendar, HelpCircle, Activity, Sun } from 'lucide-react';

interface SpecializedDiagnosticsViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle: ChartStyle;
  setChartStyle: (style: ChartStyle) => void;
  onOpenAnalytics?: () => void;
}

export const SpecializedDiagnosticsView: React.FC<SpecializedDiagnosticsViewProps> = ({
  kundali,
  lang = 'en',
  chartStyle,
  setChartStyle,
  onOpenAnalytics
}) => {
  const [activeSubSection, setActiveSubSection] = useState<
    'navtara' | 'trines' | 'rectification' | 'core-view' | 'karka' | 'muhurta' | 'prashna' | 'varshaphala' | 'dignity'
  >('navtara');

  const isHi = lang === 'hi';

  const subSections = [
    { id: 'navtara', label: isHi ? 'नवतारा चक्र' : 'Navtara Chakra', icon: Star },
    { id: 'trines', label: isHi ? 'त्रिकोण व अरिष्ट' : 'Trines & Arishta', icon: Triangle },
    { id: 'karka', label: isHi ? 'कारक विश्लेषण' : 'Karka (Significators)', icon: Award },
    { id: 'dignity', label: isHi ? 'ग्रह बल व गरिमा' : 'Planetary Dignity', icon: Activity },
    { id: 'muhurta', label: isHi ? 'मुहूर्त शोधन' : 'Muhurta Advisor', icon: Calendar },
    { id: 'prashna', label: isHi ? 'प्रश्न शास्त्र' : 'Prashna Horary', icon: HelpCircle },
    { id: 'varshaphala', label: isHi ? 'वर्षफल (ताजिका)' : 'Varshaphala (Tajika)', icon: Sun },
    { id: 'rectification', label: isHi ? 'समय शोधन' : 'Time Rectification', icon: Clock },
    { id: 'core-view', label: isHi ? 'कोर ज्योतिषी व्यू' : 'Astrologer Core View', icon: Shield }
  ];

  return (
    <div className="space-y-6">
      {/* Sub-navigation bar for Specialized Diagnostics */}
      <div className="bg-astro-bg rounded-2xl border border-astro-border p-4 shadow-xs">
        <div className="flex items-center justify-between flex-wrap gap-3 border-b border-astro-border/70 pb-3">
          <div>
            <h2 className="text-base sm:text-lg font-serif font-bold text-astro-primary">
              {isHi ? 'विशिष्ट शास्त्रीय निदान एवं उपकरण' : 'Specialized Classical Diagnostics & Tools'}
            </h2>
            <p className="text-xs text-astro-secondary-text">
              {isHi
                ? 'नवतारा, त्रिकोण, कारक, ग्रह गरिमा, प्रश्न, समय शोधन एवं कोर शास्त्रीय विश्लेषण'
                : 'Navtara Chakra, Trines, Karakas, Dignity, Horary, Rectification & Astrologer Core'}
            </p>
          </div>
        </div>

        <div className="w-full overflow-x-auto custom-horizontal-scrollbar py-2 mt-2">
          <div className="flex items-center gap-1.5 min-w-max px-0.5">
            {subSections.map((sec) => {
              const Icon = sec.icon;
              const isActive = activeSubSection === sec.id;
              return (
                <button
                  key={sec.id}
                  onClick={() => setActiveSubSection(sec.id as any)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl whitespace-nowrap text-xs font-semibold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-astro-primary text-white font-bold shadow-xs'
                      : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
                  <span>{sec.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* View Rendering */}
      {activeSubSection === 'navtara' && <NavtaraView kundali={kundali} lang={lang} />}
      {activeSubSection === 'trines' && <TrinesView kundali={kundali} lang={lang} />}
      {activeSubSection === 'karka' && <KarkaView kundali={kundali} lang={lang} />}
      {activeSubSection === 'dignity' && <PlanetStrengthDashboard kundali={kundali} lang={lang} />}
      {activeSubSection === 'muhurta' && <MuhurtaAdvisor kundali={kundali} lang={lang} />}
      {activeSubSection === 'prashna' && <PrashnaModule lang={lang} />}
      {activeSubSection === 'varshaphala' && <VarshaphalaModule lang={lang} />}
      {activeSubSection === 'rectification' && <BirthTimeRectification lang={lang} />}
      {activeSubSection === 'core-view' && (
        <AstrologerCoreView
          kundali={kundali}
          lang={lang}
          onOpenPublishedConsultation={() => onOpenAnalytics && onOpenAnalytics()}
        />
      )}
    </div>
  );
};
